import React from "react";
import { Check, Activity, Brain, Moon, HeartPulse, User } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Link } from "react-router-dom";

const Eligibility = () => {
  const criteria = [
    "Be 21 years or older",
    "Medically diagnosed with a chronic condition",
    "Not pregnant or history of heart attack/stroke (past 6 months)",
    "Tried at least two conventional treatments",
  ];

  const categories = [
    {
      name: "Pain Conditions",
      items: ["Back Pain", "Fibromyalgia", "Arthritis", "Cancer-Related Pain"],
      icon: <Activity className="w-8 h-8 text-white" />,
      color: "bg-[#2ba78c]",
    },
    {
      name: "Neurological",
      items: ["Migraine", "MS", "Parkinson's Disease"],
      icon: <Brain className="w-8 h-8 text-white" />,
      color: "bg-[#76c042]",
    },
    {
      name: "Women's Health",
      items: ["Endometriosis", "Menopause", "PMDD"],
      icon: <HeartPulse className="w-8 h-8 text-white" />,
      color: "bg-[#00aeef]",
    },
    {
      name: "Psychiatric / Neurodevelopmental",
      items: ["PTSD", "Autism", "ADHD"],
      icon: <User className="w-8 h-8 text-white" />,
      color: "bg-[#7c3aed]",
    },
    {
      name: "Sleep",
      items: ["Sleep & Insomnia"],
      icon: <Moon className="w-8 h-8 text-white" />,
      color: "bg-[#005eb8]",
    },
  ];

  const slugMap = {
    "Parkinson's Disease": "parkinsons",
    "MS": "ms",
    "PMDD": "pmdd",
    "PTSD": "ptsd",
    "Autism": "autism",
    "ADHD": "adhd",
    "Sleep & Insomnia": "sleep-insomnia",
    "Cancer-Related Pain": "cancer-related-pain",
  };

  const getLink = (item) => {
    if (slugMap[item]) return `/conditions/${slugMap[item]}`;
    return `/conditions/${item.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}`;
  };

  return (
    <section className="py-20 bg-[#f8fafc]" id="eligibility">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-12 items-start">

          {/* Left: Checklist */}
          <div className="lg:w-1/3">
            <h2 className="text-3xl font-bold text-[#012d5c] mb-8">
              Who is eligible for treatment?
            </h2>
            <div className="space-y-6">
              {criteria.map((item, index) => (
                <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                  <div className="bg-[#e8f5e9] p-2 rounded-full mr-4 shrink-0">
                    <Check className="w-5 h-5 text-[#4caf50]" />
                  </div>
                  <p className="text-lg text-gray-700 font-medium pt-1">{item}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 text-lg font-semibold text-[#005eb8]">
              Medical cannabis may be considered for a range of conditions. Check your eligibility to find out more.
            </p>
          </div>

          {/* Right: Condition Categories Grid */}
          <div className="lg:w-2/3">
            <h2 className="text-3xl font-bold text-[#012d5c] mb-8">
              Conditions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {categories.map((cat, index) => (
                <Card key={index} className="border-none shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden rounded-lg h-full bg-white">
                  <div className={`${cat.color} h-2 w-full`}></div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`${cat.color} w-12 h-12 rounded-lg flex items-center justify-center shadow-md shrink-0`}>
                        {cat.icon}
                      </div>
                      <h3 className="text-xl font-bold text-[#012d5c]">{cat.name}</h3>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {cat.items.map((item, i) => (
                        <Link
                          key={i}
                          to={getLink(item)}
                          className="bg-[#f0f4f8] hover:bg-[#e0f2fe] text-gray-700 hover:text-[#005eb8] text-xs font-semibold px-3 py-1.5 rounded-full transition-colors border border-gray-200"
                        >
                          {item}
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Eligibility;
